// askSmart-ui/src/pages/TOSP/tabs/TenantsTab.tsx
import React, { useMemo, useState } from "react";
import DeleteConfirmModal from "../../../components/DeleteConfirmModal";
import TenantModal, { TenantFormValues } from "../components/TenantModal";
import TenantGrid from "../components/TenantGrid";
import { useTospData } from "../TospDataContext";
import { currentTenants, Tenant, tenantLabel, newId } from "../types";

// Tenants — the businesses occupying units. The occupancy records on each
// tenant are the tenant↔unit relationship, and are kept when a lease ends so
// a unit's history stays intact.
export default function TenantsTab() {
  const { units, tenants, saveTenant, deleteTenant, saveOccupancy, unitById } =
    useTospData();
  // The tenant being edited, `null` for a new one, `undefined` when closed —
  // so "add" and "edit" share one modal without a second open/closed flag.
  const [editing, setEditing] = useState<Tenant | null | undefined>(undefined);
  const [viewing, setViewing] = useState<Tenant | null>(null);
  const [deleting, setDeleting] = useState<Tenant | null>(null);

  // The businesses still trading. A tenancy whose lease has ended lives on the
  // Past Tenants tab instead — listed in both places it would read as two
  // tenancies, and the count on the tab would disagree with the rows under it.
  //
  // `tenants` (the whole collection) is still what's handed to the modal below,
  // because a takeover has to be able to see the lease it is ending even if that
  // lease has since been dated.
  const current = useMemo(() => currentTenants(tenants), [tenants]);

  // A takeover dates the outgoing tenant's lease — its own record now, so this
  // writes that one occupancy rather than rewriting a whole second tenant. The
  // outgoing lease is ended first: if that fails, the unit isn't handed over.
  const handleSave = async ({ takeovers, ...values }: TenantFormValues) => {
    try {
      for (const takeover of takeovers) {
        const owner = tenants.find((t) =>
          t.occupancies.some((o) => o.id === takeover.occupancyId),
        );
        const occupancy = owner?.occupancies.find((o) => o.id === takeover.occupancyId);
        if (!owner || !occupancy) continue;
        await saveOccupancy(owner.id, { ...occupancy, leaseEndDate: takeover.endDate });
      }

      await saveTenant({ ...(editing || { id: newId("tnt") }), ...values });
      setEditing(undefined);
    } catch {
      /* surfaced by the provider */
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await deleteTenant(deleting.id);
      setDeleting(null);
    } catch {
      /* surfaced by the provider */
    }
  };

  return (
    <section className="tosp-card">
      <div className="tosp-toolbar">
        {/* No longer gated on units existing. Adding a tenant now starts from
            the lease, and a lease may demise a space that needs no unit at all
            — the units are assigned during the review, or afterwards. */}
        <button className="tosp-btn-primary" onClick={() => setEditing(null)}>
          + Add Tenant
        </button>
      </div>

      <TenantGrid
        tenants={current}
        emptyMessage={'No tenants yet. Click "+ Add Tenant" to create your first tenant.'}
        onView={setViewing}
        onEdit={setEditing}
        onDelete={setDeleting}
      />

      <TenantModal
        isOpen={editing !== undefined}
        tenant={editing ?? null}
        units={units}
        tenants={tenants}
        unitById={unitById}
        onClose={() => setEditing(undefined)}
        onSave={handleSave}
      />

      {/* View is the same form in read-only mode, so the two can't drift. */}
      <TenantModal
        isOpen={!!viewing}
        tenant={viewing}
        units={units}
        tenants={tenants}
        unitById={unitById}
        onClose={() => setViewing(null)}
        readOnly
      />

      <DeleteConfirmModal
        isOpen={!!deleting}
        title="Delete Tenant"
        message={
          deleting
            ? `Are you sure you want to delete "${tenantLabel(deleting)}"? Its ${deleting.occupancies.length} occupancy record${
                deleting.occupancies.length === 1 ? "" : "s"
              } will be removed and this cannot be undone.`
            : ""
        }
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
      />
    </section>
  );
}
